/**
 * Per-state public records landing pages.
 *
 * Each state with county hubs or statewide sources gets its own indexable URL
 * (`/public-records/tx`), derived from the same seeded data the directory uses,
 * so the sitemap and the pages can never drift apart.
 */
import { COUNTY_RECORD_HUBS, type CountyRecordHub } from "@/data/countyRecordHubs";
import { PUBLIC_RECORDS, type PublicRecordSource } from "@/data/publicRecordsDirectory";
import { computeStateCoverage, type StateCoverage } from "@/lib/public-records-coverage";
import type { SitemapEntry } from "@/lib/seo-routes";

export const PUBLIC_RECORDS_STATES: StateCoverage[] = computeStateCoverage();

/** "TX" -> "/public-records/tx" */
export function publicRecordsStatePath(code: string): string {
  return `/public-records/${code.toLowerCase()}`;
}

export function findStateBySlug(slug: string | undefined): StateCoverage | undefined {
  if (!slug) return undefined;
  const normalized = slug.trim().toLowerCase();
  return PUBLIC_RECORDS_STATES.find((s) => s.code.toLowerCase() === normalized);
}

export function hubsForState(code: string): CountyRecordHub[] {
  return COUNTY_RECORD_HUBS.filter((h) => h.state === code).sort((a, b) =>
    a.county.localeCompare(b.county),
  );
}

/** Statewide sources for a state, plus the national datasets that also apply. */
export function sourcesForState(code: string): PublicRecordSource[] {
  return PUBLIC_RECORDS.filter((r) => r.state === code || r.state === "US");
}

/** Sitemap rows for every state landing page. */
export const PUBLIC_RECORDS_STATE_ENTRIES: SitemapEntry[] = PUBLIC_RECORDS_STATES.map((s) => ({
  path: publicRecordsStatePath(s.code),
  changefreq: "monthly" as const,
  priority: "0.6",
}));
